import { Button, Space, message } from "antd";
import { DetailPageErrorState } from "@/views/components/DetailPageErrorState";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    MultiGrid as ZhMultiGrid
} from "zhihao-ui-react";

import request, { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { confirmDialog } from "@/utils/confirmDialog";
import type { OperationHistory } from "@/entity/logistics/operationHistory";
import { Operation_History_Enum } from "@/meta/logistics/operationHistory";
import { operationHistoryService } from "@/api/services/operationHistoryService";
import { OIL_ORDER_PAY_STATE, OIL_ORDER_STATE, OIL_ORDER_TYPE, OIL_TYPE, TIME_PERIOD } from "@zhihaoscm/meta";
import { OilOrderPlanDialog } from "./OilOrderPlanDialog";
import { OilOrderConfirmDialog, type OilOrderConfirmForm } from "./OilOrderConfirmDialog";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

export interface OilOrderDetailData {
    oilOrder: {
        id: string | number;
        shipId?: string | number;
        shipCnName?: string;
        shipName?: string;
        captainName?: string;
        captainMobile?: string;
        captainInfo?: { name?: string; realName?: string; mobile?: string; code?: string };
        state?: number;
        orderType?: number;
        isAgreeProtocol?: boolean;
        bookingTime?: string;
        bookingRefuelingTime?: string;
        bookingRefuelingTimePeriod?: string | number;
        bookingRefuelingPrice?: number;
        bookingRefuelingVolume?: number;
        bookingRefuelingCost?: number;
        oilType?: string | number;
        remark?: string;
        planInfo?: {
            planByName?: string;
            planTime?: string;
            refuelingTime?: string;
            refuelingTimePeriod?: string | number;
            refuelingVolume?: number;
            refuelingPrice?: number;
            refuelingCost?: number;
        };
        actualRefuelingTime?: string;
        payState?: string | number;
        payAmount?: number;
        payFileIds?: Array<string | number>;
    };
    oilSite?: { id?: string | number; name?: string; address?: string; brand?: string; phone?: string };
}

export interface OilOrderDetailPageProps {
    loadDetail?: (id: string) => Promise<OilOrderDetailData>;
    completeOil?: (id: string | number) => Promise<unknown>;
    planOrder?: (id: string | number, form: OilOrderPlanForm) => Promise<unknown>;
    confirmOrder?: (id: string | number, form: OilOrderConfirmForm) => Promise<unknown>;
    cancelOrder?: (id: string | number) => Promise<unknown>;
    deleteOrder?: (id: string | number) => Promise<unknown>;
    loadPaymentFile?: (id: string | number) => Promise<{ path?: string }>;
    loadOperationHistory?: (bizNo: string) => Promise<OperationHistory[]>;
}

export interface OilOrderPlanForm {
    oilSiteId: string | number;
    planInfo: {
        oilType?: number;
        refuelingVolume?: number;
        refuelingTime?: string;
        refuelingTimePeriod?: string | number;
        refuelingPrice?: number;
    };
}

export type { OilOrderConfirmForm } from "./OilOrderConfirmDialog";

/** 复用 Vue 油品订单详情接口。 */
export async function loadOilOrderDetail(id: string): Promise<OilOrderDetailData> {
    const response = await shippingRequestHttp.service.get<OilOrderDetailData>(
        `/oil-order-snapshot/vo/${encodeURIComponent(id)}`
    );
    return response.data;
}

/** 复用 Vue 油品订单完成接口。 */
export async function completeOilOrderDetail(id: string | number) {
    const response = await shippingRequestHttp.service.put(`/oil-order-snapshot/complete/${id}`);
    return response.data;
}

/** 复用 Vue 油品订单报计划接口。 */
export async function planOilOrderDetail(id: string | number, form: OilOrderPlanForm) {
    const response = await shippingRequestHttp.service.put(`/oil-order-snapshot/plan/${id}`, form);
    return response.data;
}

/** 复用 Vue 油品订单确认加油接口。 */
export async function confirmOilOrderDetail(id: string | number, form: OilOrderConfirmForm) {
    const response = await shippingRequestHttp.service.put(`/oil-order-snapshot/confirm-refueling/${id}`, form);
    return response.data;
}

/** 复用 Vue 油品订单取消接口。 */
export async function cancelOilOrderDetail(id: string | number) {
    const response = await shippingRequestHttp.service.put(`/oil-order-snapshot/cancel/${id}`);
    return response.data;
}

/** 复用 Vue 油品订单删除接口。 */
export async function deleteOilOrderDetail(id: string | number) {
    const response = await shippingRequestHttp.service.delete(`/oil-order-snapshot/delete/${id}`);
    return response.data;
}

/** 复用 Vue 付款凭证预览接口，返回可直接打开的文件地址。 */
export async function loadOilOrderPaymentFile(id: string | number): Promise<{ path?: string }> {
    const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${Number(id)}`);
    return response.data;
}

/** 复用 Vue 油品订单操作历史接口，固定油品订单模块值 5。 */
export async function loadOilOrderOperationHistory(bizNo: string): Promise<OperationHistory[]> {
    return operationHistoryService.getDetail({ bizNo, module: Operation_History_Enum.OIL_ORDER });
}

const valueText = (value?: string | number | null) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);

const enumText = (
    meta: { getIntroByCode: (code: number | string | undefined) => string | null },
    value?: number | string
) => meta.getIntroByCode(value) || DEFAULT_VALUE;

const moneyText = (value?: number, unit = "") => (value === undefined ? "-" : `${value}${unit}`);

function historyDateText(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

/** 展示 Vue 油品订单详情中的操作历史，并保留字段变更展开行为。 */
function OilOrderHistory({ logs }: { logs: OperationHistory[] }) {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    if (!logs.length) return <span>暂无操作记录</span>;

    return (
        <ol aria-label="油品订单操作记录" className="m-0 list-none p-0">
            {logs.map((log, index) => {
                const key = String(log.id || index);
                const changes = Array.isArray(log.operationContent) ? log.operationContent : [];
                const isExpanded = Boolean(expanded[key]);

                return (
                    <li key={key} className="mb-3">
                        <div className="flex items-start gap-2">
                            <span>{index + 1}.</span>
                            <span>{historyDateText(log.createdTime)}</span>
                            <span>{`${log.operationAction || DEFAULT_VALUE}。`}</span>
                            {changes.length ? (
                                <Button
                                    aria-label={isExpanded ? "收起操作详情" : "展开操作详情"}
                                    size="small"
                                    type="link"
                                    onClick={() => setExpanded(current => ({ ...current, [key]: !isExpanded }))}
                                >
                                    {isExpanded ? "收起" : "详情"}
                                </Button>
                            ) : null}
                        </div>
                        {isExpanded ? (
                            <div className="ml-6 mt-2">
                                {changes.map((change, changeIndex) => (
                                    <div key={`${key}-${changeIndex}`}>
                                        修改了 <strong>{change.key || DEFAULT_VALUE}</strong>，旧值为“
                                        {change.oldValue || DEFAULT_VALUE}”，新值为“{change.newValue || DEFAULT_VALUE}
                                        ”。
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </li>
                );
            })}
        </ol>
    );
}

/** 油品订单详情，保留 Vue 详情分区和订单状态操作边界。 */
export function OilOrderDetailPage({
    loadDetail = loadOilOrderDetail,
    planOrder = planOilOrderDetail,
    confirmOrder = confirmOilOrderDetail,
    completeOil = completeOilOrderDetail,
    cancelOrder = cancelOilOrderDetail,
    deleteOrder = deleteOilOrderDetail,
    loadPaymentFile = loadOilOrderPaymentFile,
    loadOperationHistory = loadOilOrderOperationHistory
}: OilOrderDetailPageProps) {
    const location = useLocation();
    const { id: routeId } = useParams();
    const id = routeId || location.pathname.split("/").filter(Boolean).pop() || "";
    const navigate = useNavigate();
    const [data, setData] = useState<OilOrderDetailData>();
    const [operationHistory, setOperationHistory] = useState<OperationHistory[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [planOpen, setPlanOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (!disposed) setData(result);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("油品订单详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    useEffect(() => {
        if (!data?.oilOrder.id) {
            setOperationHistory([]);
            return;
        }

        let disposed = false;
        setOperationHistory([]);
        // Vue 详情页按订单编号和油品订单模块查询操作历史，避免展示其他单据的记录。
        void loadOperationHistory(String(data.oilOrder.id))
            .then(result => {
                if (!disposed) setOperationHistory(result || []);
            })
            .catch(() => {
                if (!disposed) setOperationHistory([]);
            });

        return () => {
            disposed = true;
        };
    }, [data, loadOperationHistory]);

    const runAction = async (
        action: () => Promise<unknown>,
        successText: string,
        remove = false,
        confirmMessage?: string
    ) => {
        if (confirmMessage && !(await confirmDialog(confirmMessage))) return;
        try {
            await action();
            notifyOperationSuccess(successText);
            if (remove) navigate(-1);
            else setData(await loadDetail(id));
        } catch {
            setErrorMessage("油品订单操作失败");
        }
    };

    if (errorMessage)
        return (
            <DetailPageErrorState
                ariaLabel="oil-order-detail-page"
                message={errorMessage}
                onBack={() => navigate(-1)}
            />
        );
    if (!data) return <section aria-label="oil-order-detail-page" />;

    const order = data.oilOrder;
    const shipName = order.shipCnName || order.shipName;
    const captainInfo = order.captainInfo;
    // 预约订单的核对规则沿用 Vue：平台代下必须已同意协议，自行下单无需额外判断。
    const canCheck =
        order.state === OIL_ORDER_STATE.BOOKING.code &&
        (order.orderType !== OIL_ORDER_TYPE.PLATFORM_ORDER.code || order.isAgreeProtocol);
    const bookingTime = `${valueText(order.bookingRefuelingTime)} ${enumText(TIME_PERIOD, order.bookingRefuelingTimePeriod)}`;
    const planTime = order.planInfo
        ? `${valueText(order.planInfo.refuelingTime)} ${enumText(TIME_PERIOD, order.planInfo.refuelingTimePeriod)}`
        : DEFAULT_VALUE;

    /** 打开旧 Vue 付款凭证的查看/下载地址。 */
    const previewPaymentFile = async (fileId: string | number) => {
        try {
            const file = await loadPaymentFile(fileId);
            if (file.path) window.open(file.path, "_blank");
        } catch {
            setErrorMessage("付款凭证加载失败");
        }
    };

    return (
        <section aria-label="oil-order-detail-page" className="flex min-h-full flex-col gap-4">
            <div className="section-card">
                <ZhDetailHeader
                    leftInfos={[
                        { label: "编号", value: valueText(order.id) },
                        { label: "订单方式", value: enumText(OIL_ORDER_TYPE, order.orderType) }
                    ]}
                    rightInfos={[{ label: "下单时间", value: valueText(order.bookingTime) }]}
                    stateDesc={enumText(OIL_ORDER_STATE, order.state)}
                    title="订单详情"
                />
            </div>
            <ZhMultiGrid>
                <div className="section-card">
                    <div className="section-title">预约信息</div>
                    <ZhGrid cols={4} gap={[32, 32]}>
                        <ZhBaseItem label="预约船舶" value={valueText(shipName)} />
                        <ZhBaseItem label="MMSI" span={3} value={valueText(order.shipId)} />
                        <ZhBaseItem
                            label="承运商账号"
                            value={[captainInfo?.name || captainInfo?.realName, captainInfo?.mobile, captainInfo?.code]
                                .map(valueText)
                                .join(" | ")}
                        />
                        <ZhBaseItem label="联系人" value={valueText(order.captainName)} />
                        <ZhBaseItem label="联系方式" value={valueText(order.captainMobile)} />
                    </ZhGrid>
                </div>
                <div className="section-card">
                    <div className="section-title">加油信息</div>
                    <ZhGrid cols={4} gap={[32, 32]}>
                        <ZhBaseItem label="油品类型" value={enumText(OIL_TYPE, order.oilType)} />
                        <ZhBaseItem label="预估油价" value={moneyText(order.bookingRefuelingPrice, "元/吨")} />
                        <ZhBaseItem label="预估加油量" span={2} value={moneyText(order.bookingRefuelingVolume, "吨")} />
                        <ZhBaseItem label="预估油费" value={moneyText(order.bookingRefuelingCost, "元")} />
                        <ZhBaseItem label="预约时间" span={3} value={bookingTime} />
                        <ZhBaseItem label="预约站点" value={valueText(data.oilSite?.name)} />
                        <ZhBaseItem label="站点地址" value={valueText(data.oilSite?.address)} />
                        <ZhBaseItem label="品牌" value={valueText(data.oilSite?.brand)} />
                        <ZhBaseItem label="站点联系方式" value={valueText(data.oilSite?.phone)} />
                        <ZhBaseItem label="备注" span={4} value={valueText(order.remark)} />
                    </ZhGrid>
                </div>
                {order.planInfo ? (
                    <div className="section-card">
                        <div className="section-title">计划信息</div>
                        <ZhGrid cols={4} gap={[32, 32]}>
                            <ZhBaseItem label="计划人" value={valueText(order.planInfo.planByName)} />
                            <ZhBaseItem label="计划时间" value={valueText(order.planInfo.planTime)} />
                            <ZhBaseItem label="计划加油时间" span={2} value={planTime} />
                            <ZhBaseItem label="加油量" value={moneyText(order.planInfo.refuelingVolume, "吨")} />
                            <ZhBaseItem label="油价" value={moneyText(order.planInfo.refuelingPrice, "元/吨")} />
                            <ZhBaseItem label="油费" value={moneyText(order.planInfo.refuelingCost, "元")} />
                            {order.actualRefuelingTime ? (
                                <ZhBaseItem label="实际加油时间" value={valueText(order.actualRefuelingTime)} />
                            ) : null}
                        </ZhGrid>
                    </div>
                ) : null}
                {order.payState ? (
                    <div className="section-card">
                        <div className="section-title">支付信息</div>
                        <ZhGrid cols={4} gap={[32, 32]}>
                            <ZhBaseItem label="付款金额" value={moneyText(order.payAmount, "元")} />
                            <ZhBaseItem label="支付状态" value={enumText(OIL_ORDER_PAY_STATE, order.payState)} />
                            {order.payFileIds?.length ? (
                                order.payFileIds.map((fileId, index) => {
                                    const label =
                                        order.payFileIds && order.payFileIds.length > 1
                                            ? `付款凭证${index + 1}`
                                            : "付款凭证";
                                    return (
                                        <ZhBaseItem key={String(fileId)} label={label}>
                                            <Button
                                                aria-label={`查看/下载${label}`}
                                                type="link"
                                                onClick={() => void previewPaymentFile(fileId)}
                                            >
                                                查看/下载
                                            </Button>
                                        </ZhBaseItem>
                                    );
                                })
                            ) : (
                                <ZhBaseItem label="付款凭证" span={2} value={DEFAULT_VALUE} />
                            )}
                        </ZhGrid>
                    </div>
                ) : null}
                {operationHistory.length ? (
                    <div className="section-card">
                        <div className="section-title">历史记录</div>
                        <OilOrderHistory logs={operationHistory} />
                    </div>
                ) : null}
            </ZhMultiGrid>
            <div className="section-card">
                <Space wrap>
                    {canCheck ? (
                        <Button onClick={() => navigate(`/logistics/oil/oilTypeManagement/check/${order.id}`)}>
                            核对
                        </Button>
                    ) : null}
                    {order.state === OIL_ORDER_STATE.IN_PROGRESS.code && !order.planInfo ? (
                        <Button onClick={() => setPlanOpen(true)}>报计划</Button>
                    ) : null}
                    {order.state === OIL_ORDER_STATE.IN_PROGRESS.code &&
                    order.planInfo &&
                    !order.actualRefuelingTime ? (
                        <Button
                            onClick={() => {
                                if (Number(order.payState) !== OIL_ORDER_PAY_STATE.SUCCESS.code) {
                                    message.warning("此油品订单油费待确认，请确认财税收到此油款");
                                    return;
                                }
                                setConfirmOpen(true);
                            }}
                        >
                            确认加油
                        </Button>
                    ) : null}
                    {order.state === OIL_ORDER_STATE.IN_PROGRESS.code && order.actualRefuelingTime ? (
                        <Button onClick={() => void runAction(() => completeOil(order.id), "已完成加油")}>
                            完成加油
                        </Button>
                    ) : null}
                    {order.state === OIL_ORDER_STATE.BOOKING.code ||
                    order.state === OIL_ORDER_STATE.IN_PROGRESS.code ? (
                        <Button
                            onClick={() =>
                                void runAction(() => cancelOrder(order.id), "已取消", false, "是否取消该油品订单？")
                            }
                        >
                            取消
                        </Button>
                    ) : null}
                    {order.state === OIL_ORDER_STATE.CANCELED.code ? (
                        <Button
                            onClick={() =>
                                void runAction(() => deleteOrder(order.id), "已删除", true, "是否删除该油品订单？")
                            }
                        >
                            删除
                        </Button>
                    ) : null}
                    <Button type="primary" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Space>
            </div>
            <OilOrderPlanDialog
                open={planOpen}
                row={{ oilOrder: order, oilSite: data.oilSite }}
                onCancel={() => setPlanOpen(false)}
                onSubmit={async form => {
                    await runAction(() => planOrder(order.id, form), "已报计划");
                    setPlanOpen(false);
                }}
            />
            <OilOrderConfirmDialog
                open={confirmOpen}
                onCancel={() => setConfirmOpen(false)}
                onSubmit={async form => {
                    await runAction(() => confirmOrder(order.id, form), "已确认加油");
                    setConfirmOpen(false);
                }}
            />
        </section>
    );
}

export default OilOrderDetailPage;
